import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_AVAILABILITY, effectiveIntervals, LocalDate } from '@acs/shared';
import type { AppointmentDoc } from '../../shared/api/converters.js';
import { useI18n, type TranslationKey } from '../../i18n/index.js';
import { useMediaQuery } from '../../shared/lib/useMediaQuery.js';
import { PHONE_QUERY } from '../../shared/lib/breakpoints.js';
import { BrandLoader, Button, Icon, IconButton } from '@acs/ui';
import {
  DAY_MIN,
  TimeGrid,
  addDays,
  assignLanes,
  labelFor,
  WEEK_HEAD_PX,
  dateRangeYearLabel,
  minToClock,
  mondayOf,
  normalizeDay,
  todayIsoIn,
  fitWindow,
  weekdayDateYearLabel,
  zonedDateAndMinute,
  type GridInterval,
  type TimeGridBlock,
  type TimeGridColumn,
} from '../schedule-grid/index.js';
import { useAvailability } from '../settings/hooks.js';
import { useWeekAppointments } from './hooks.js';
import { BookingDialog } from './BookingDialog.js';

/** URL parameter holding the day the calendar shows (absent = today). The
 * dashboard reads it too: New booking opens on that day (doc 86). */
export const DAY_PARAM = 'day';
const isDay = (v: string | null): v is string => v !== null && LocalDate.safeParse(v).success;

/** Minimum pixels per hour of the phone's single-day grid. */
const PHONE_HOUR_PX = 64;

/**
 * The dashboard week calendar: the read-only TimeGrid over real dates in the
 * BUSINESS timezone. Tinted `open` blocks highlight working hours (business
 * fallback availability, doc 09 D2); `busy` blocks are confirmed bookings,
 * laid out side-by-side when staff members overlap. Clicking a booking opens a
 * dialog with its full details.
 */
export function WeekCalendar({
  businessId,
  timezone,
  slotStepMin,
}: {
  businessId: string;
  timezone: string;
  /** Booking step, for the Change time form (doc 43 D12). */
  slotStepMin: number;
}) {
  const { t, tag } = useI18n();
  const today = todayIsoIn(timezone);
  const nowMin = useNowMinute(timezone, today);
  // Mobile shows a single day and pages day-by-day; desktop shows the whole
  // Monday-aligned week. `start` is the first visible date under each mode.
  const mobile = useMediaQuery(PHONE_QUERY);
  const days = mobile ? 1 : 7;
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // The day the owner is looking at lives in the URL (`?day=`), so a refresh —
  // or a link — lands on the same day (phone) or the week containing it
  // (desktop), instead of jumping back to today. Today is the absence of the
  // parameter, so the ordinary URL stays clean. One anchor serves both modes:
  // crossing the breakpoint shows that day, or its week, rather than resetting.
  const [params, setParams] = useSearchParams();
  const requested = params.get(DAY_PARAM);
  const anchor = isDay(requested) ? requested : today;
  const start = mobile ? anchor : mondayOf(anchor);
  const goTo = (day: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (day === today) next.delete(DAY_PARAM);
        else next.set(DAY_PARAM, day);
        return next;
      },
      { replace: true },
    );

  const dates = Array.from({ length: days }, (_, i) => addDays(start, i));
  const step = (dir: -1 | 1) => goTo(addDays(anchor, dir * days));
  const pickerRef = useRef<HTMLInputElement>(null);
  // Where `showPicker` is missing (older Safari), a programmatic click opens
  // nothing — so the button turns into a visible date field instead.
  const [inlinePicker, setInlinePicker] = useState(false);
  const openPicker = () => {
    const input = pickerRef.current;
    if (input && typeof input.showPicker === 'function') {
      try {
        input.showPicker();
        return;
      } catch {
        /* not allowed here — fall through to the visible field */
      }
    }
    setInlinePicker(true);
  };

  const { data: avail } = useAvailability(businessId);
  // Always fetch the padded week containing the visible range, so paging within
  // a week on mobile reuses the cache; appointments outside `dates` are ignored.
  const { data: appointments, isLoading } = useWeekAppointments(businessId, mondayOf(start));
  // The weeks either side are subscribed too, so a step back or forward lands
  // on a listener that has already answered instead of starting one and
  // waiting on the server. A few small documents each; the cost is nothing
  // next to a calendar that goes blank on every page turn.
  useWeekAppointments(businessId, addDays(mondayOf(start), -7));
  useWeekAppointments(businessId, addDays(mondayOf(start), 7));
  const business = avail ?? DEFAULT_AVAILABILITY;

  const openByDate = new Map<string, GridInterval[]>(
    dates.map((date) => [date, normalizeDay(effectiveIntervals(null, business, date).map((iv) => ({ ...iv })))]),
  );

  const byDate = new Map<string, { appt: AppointmentDoc; startMin: number; endMin: number }[]>();
  const apptById = new Map<string, AppointmentDoc>();
  for (const appt of appointments ?? []) {
    const { date, min } = zonedDateAndMinute(appt.start, timezone);
    if (!openByDate.has(date)) continue; // padding day outside the visible week
    const durMin = Math.max(1, Math.round((appt.end.getTime() - appt.start.getTime()) / 60000));
    const list = byDate.get(date) ?? [];
    list.push({ appt, startMin: min, endMin: Math.min(DAY_MIN, min + durMin) });
    byDate.set(date, list);
    apptById.set(appt.id, appt);
  }
  const selected = selectedId ? apptById.get(selectedId) ?? null : null;

  const columns: TimeGridColumn[] = dates.map((date) => ({
    id: date,
    label: <DayHeading date={date} today={date === today} locale={tag} todayLabel={t('calendar.todaySuffix')} />,
    readOnly: true,
    highlight: date === today,
    nowMin: date === today ? nowMin : undefined,
    past: date < today,
    weekend: isWeekend(date),
    closed: openByDate.get(date)!.length === 0,
    badge:
      openByDate.get(date)!.length === 0 ? <span className="week-closed">{t('calendar.closed')}</span> : undefined,
  }));

  const openBlocks: TimeGridBlock[] = dates.flatMap((date) =>
    openByDate.get(date)!.map((iv) => ({
      id: `open:${date}#${iv.startMin}`,
      columnId: date,
      kind: 'open' as const,
      ...iv,
    })),
  );

  // The bubble says there is a message; the tooltip says what kind — even
  // when the caller left no summary, only "wants a call back".
  const messageTooltip = (m: NonNullable<AppointmentDoc['message']>) =>
    [t(`message.kind.${m.kind}` as TranslationKey), m.callbackRequested ? t('message.callback') : '', m.summary]
      .filter(Boolean)
      .join(' · ');

  const busyBlocks: TimeGridBlock[] = dates.flatMap((date) => {
    const list = (byDate.get(date) ?? []).sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
    const lanes = assignLanes(list);
    return list.map(({ appt, startMin, endMin }, i) => {
      const who = appt.staffName ? ` · ${appt.staffName}` : '';
      // "9:30", not "09:30": on a calendar the padded hour is form-field noise.
      const when = `${minToClock(startMin)}–${minToClock(endMin)}`;
      return {
        id: `appt:${appt.id}`,
        columnId: date,
        kind: 'busy' as const,
        startMin,
        endMin,
        // Time, then the service — what you scan a day for; who it's with
        // reads underneath. A caller's message shows as a speech bubble on the
        // right; its text is in the tooltip and the dialog (doc 43).
        label: `${when} · ${appt.serviceName}`,
        subLabel: `${appt.customerName}${who}`,
        aside: appt.message ? (
          <>
            <Icon name="message-circle" size={14} />
            <span className="tc-visually-hidden">{t('calendar.hasMessage')}</span>
          </>
        ) : undefined,
        title: t('calendar.blockTitle', {
          from: minToClock(startMin),
          to: minToClock(endMin),
          service: `${appt.serviceName}${who}`,
          customer: appt.customerName,
        }) + (appt.message ? `\n${messageTooltip(appt.message)}` : ''),
        ...lanes[i]!,
      };
    });
  });

  // Working hours and bookings plus one empty hour either side — no more, so
  // the bookings get the height (doc 43 D17).
  const win = fitWindow(dates.map((date) => [...openByDate.get(date)!, ...(byDate.get(date) ?? [])]));

  // Stretch the grid to fill its flex slot: measure the available height and
  // derive pixels-per-hour so header + canvas exactly fill it (no page scroll).
  // The grid itself is absolutely positioned inside `.week-grid-fill` (see CSS)
  // so resizing it can never change the measured height — no feedback loop.
  const gridWrapRef = useRef<HTMLDivElement>(null);
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const [hourPx, setHourPx] = useState(48);
  useLayoutEffect(() => {
    const el = gridWrapRef.current;
    if (!el) return;
    const spanHours = (win.toMin - win.fromMin) / 60;
    const measure = () => {
      const avail = el.clientHeight - WEEK_HEAD_PX;
      if (avail <= 0 || spanHours <= 0) return;
      const next = Math.max(28, avail / spanHours);
      // Ignore sub-pixel jitter so we never re-render in a tight loop.
      setHourPx((prev) => (Math.abs(prev - next) > 0.5 ? next : prev));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [win.fromMin, win.toMin]);

  // Disabled only when there is nothing to reset: a picked day inside this
  // week still lives in the URL, and Today is how the owner clears it.
  const atToday = anchor === today;

  return (
    <div className="week-calendar">
      <div className="week-pager">
        {/* The range is also the way to jump: it opens a calendar, so a date
            three months out is one pick rather than a dozen arrow clicks. */}
        <span className="week-pager-pick">
          {inlinePicker ? (
            <input
              type="date"
              className="tc-input week-pager-inline"
              aria-label={t('calendar.pickDate')}
              autoFocus
              value={anchor}
              onChange={(e) => {
                if (isDay(e.target.value)) goTo(e.target.value);
              }}
              onBlur={() => setInlinePicker(false)}
            />
          ) : (
            <button
              type="button"
              className="week-pager-range"
              // The visible range IS the accessible name (it says where you are);
              // what the button does is the tooltip.
              title={t('calendar.pickDate')}
              onClick={openPicker}
            >
              <Icon name="calendar" size={16} />
              <span className="week-pager-range__text">
                {mobile ? weekdayDateYearLabel(start, tag) : dateRangeYearLabel(start, addDays(start, days - 1), tag)}
              </span>
              <Icon name="chevron-down" size={14} />
            </button>
          )}
          <input
            ref={pickerRef}
            type="date"
            className="week-pager-input"
            tabIndex={-1}
            aria-hidden
            value={anchor}
            onChange={(e) => {
              if (isDay(e.target.value)) goTo(e.target.value);
            }}
          />
        </span>
        {isLoading ? <BrandLoader size={18} /> : null}
        <span className="spacer" />
        <div className="week-pager-nav">
          <IconButton
            size="sm"
            aria-label={mobile ? t('calendar.previousDay') : t('calendar.previousWeek')}
            icon={<Icon name="chevron-left" size={18} />}
            onClick={() => step(-1)}
          />
          <Button size="sm" variant="secondary" disabled={atToday} onClick={() => goTo(today)}>
            {t('calendar.today')}
          </Button>
          <IconButton
            size="sm"
            aria-label={mobile ? t('calendar.nextDay') : t('calendar.nextWeek')}
            icon={<Icon name="chevron-right" size={18} />}
            onClick={() => step(1)}
          />
        </div>
      </div>

      <div
        className={mobile ? 'week-grid-fill week-grid-fill--phone' : 'week-grid-fill'}
        // A phone has no viewport-high slot to fill — the dashboard is page flow
        // there — so the day gets the height its hours need at a readable scale
        // (a 30-minute booking tall enough to keep its second line), and never
        // less than the 58vh an almost-empty day used to get. The measuring
        // effect above then derives `hourPx` from this box like on desktop.
        style={
          mobile
            ? { height: `max(58vh, ${Math.round(WEEK_HEAD_PX + ((win.toMin - win.fromMin) / 60) * PHONE_HOUR_PX)}px)` }
            : undefined
        }
        ref={gridWrapRef}
        onTouchStart={(e) => {
          const touch = e.touches[0];
          swipeStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
        }}
        onTouchEnd={(e) => {
          const from = swipeStart.current;
          swipeStart.current = null;
          if (!from) return;
          const touch = e.changedTouches[0];
          const dx = (touch?.clientX ?? from.x) - from.x;
          const dy = (touch?.clientY ?? from.y) - from.y;
          // A deliberate HORIZONTAL swipe pages the calendar; a tap (small dx)
          // still falls through to a booking's click handler.
          //
          // The vertical test is the fix for a phone that could not scroll past
          // the calendar: a finger dragged down the grid to scroll the page
          // drifts sideways, and with only `|dx| > 45` to go on, that drift paged
          // the day underneath the scroll. Now the gesture has to be mostly
          // sideways to count — and `touch-action: pan-y` on the read-only canvas
          // (see `.timegrid-canvas.is-readonly`) is what lets the scroll happen
          // at all.
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) step(dx < 0 ? 1 : -1);
        }}
      >
        {/* One day on a phone, the week on a wide screen — the SAME grid either
            way. A phone used to swap a day with bookings for an agenda list,
            which meant the owner saw a calendar on an empty day and a list on a
            busy one: the bookings never landed on the timeline, which is the
            one thing a calendar view is opened to show. */}
        <TimeGrid
          columns={columns}
          blocks={[...openBlocks, ...busyBlocks]}
          window={win}
          hourPx={hourPx}
          headPx={WEEK_HEAD_PX}
          readOnly
          onCommit={() => {}}
          onActivate={(id) => setSelectedId(id.replace(/^appt:/, ''))}
        />
      </div>

      <p className="week-calendar-legend muted">
        {(
          [
            ['open', 'calendar.legend.workingHours'],
            ['busy', 'calendar.legend.booked'],
            ['off', 'calendar.legend.offHours'],
            ['closed', 'calendar.legend.dayOff'],
            ['past', 'calendar.legend.past'],
          ] as const
        ).map(([swatch, label]) => (
          <span key={swatch} className="legend-item">
            <span className={`legend-swatch legend-swatch-${swatch}`} aria-hidden /> {t(label)}
          </span>
        ))}
      </p>

      {selected && (
        <BookingDialog
          businessId={businessId}
          appt={selected}
          timezone={timezone}
          slotStepMin={slotStepMin}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

/** Two-line column heading: weekday over the date, today's number circled. The
 *  number is the larger of the two — it is what you scan a calendar for, and the
 *  weekday above it is a label on the column rather than the thing being read. */
function DayHeading({
  date,
  today,
  locale,
  todayLabel,
}: {
  date: string;
  today: boolean;
  locale: string;
  todayLabel: string;
}) {
  const [, , day] = date.split('-');
  return (
    <span className="week-day">
      <span className="week-day__name">{labelFor(date, locale).replace(/[\s\d]+$/u, '')}</span>
      <span className="week-day__num">{Number(day)}</span>
      <span className="tc-visually-hidden">{today ? todayLabel : ''}</span>
    </span>
  );
}

/** The current minute of the day in the business timezone, refreshed every
 *  30 s — drives the "now" line on today's column. */
function useNowMinute(timezone: string, today: string): number | undefined {
  const read = () => {
    const { date, min } = zonedDateAndMinute(new Date(), timezone);
    return date === today ? min : undefined;
  };
  const [min, setMin] = useState(read);
  useEffect(() => {
    setMin(read());
    const id = setInterval(() => setMin(read()), 30_000);
    return () => clearInterval(id);
  }, [timezone, today]);
  return min;
}

/** Saturday/Sunday, from the ISO date alone (no timezone maths needed). */
function isWeekend(iso: string): boolean {
  const [y, m, d] = iso.split('-').map(Number);
  const wd = new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay();
  return wd === 0 || wd === 6;
}
